import { Table, Button, Pagination, TextLink, Spin, Empty } from '@bfe/moly';
import dayjs from 'dayjs';
import { getReportList } from '@/services/home';
import { useRequest } from 'ahooks';
import { useCallback, useMemo, useState } from 'react';
import { StatusTag } from '@/components/status-tag';
import { justFormatNumber } from '@web3/base-utils';
import { maskAddress } from '@/utils';
import { ShareIcon } from '@bfe/moly-icons';
import { useFm } from '@web3/base-hooks';
import { SupportStatus } from '@/types/home';
import { formatTimeDiff } from '@/utils/day';
import { Avatar } from '@/components/avatar';
import { DetailModal } from '@/containers/detail/content/DetailModal';
import { Report } from '@/types/detailAll';
import { getReportStatusColor } from '@/containers/detail/utils';
import { getReportStatusText } from '@/containers/detail/utils';
import { CommonLoading } from '@/components/CommonLoading';
// 黑客地址详情页
const hackerAddressDetailUrlPrefix = 'https://intel.arkm.com/explorer/address/';

export const VerifiedReport = () => {
  const pageSize = 10;
  const fm = useFm();
  const [page, setPage] = useState<number>(1);
  const [visible, setVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState<Report>({} as Report);
  const { data, loading } = useRequest(
    async () => {
      const res = await getReportList({ page, pageSize });
      return res.result;
    },
    {
      refreshDeps: [page],
    }
  );

  const onClose = () => {
    setVisible(false);
  };

  const reportList = useMemo(() => {
    return data?.dataList || [];
  }, [data]);

  const pageInfo = useMemo(() => {
    return data?.pageInfo;
  }, [data]);

  const onChangePageNo = useCallback((page: number) => {
    setPage(page);
  }, []);

  const toDetail = (data) => {
    setCurrentReport(data);
    setVisible(true);
  };

  const columns = [
    {
      accessorKey: 'reportTime',
      header: fm('time'),
      cell: ({ row }) => {
        const time = row.getValue('reportTime');
        return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
      },
    },
    {
      accessorKey: 'name',
      header: fm('endPoint'),
      cell: ({ row }) => {
        const { entityName, logo } = row?.original || {};
        return (
          <div className="flex items-center gap-2">
            <Avatar src={logo} size="24" name={entityName} />
            <div className="font-semibold text-sm">{entityName ?? '--'}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'fundAmount',
      header: fm('receivedFunds'),
      cell: ({ row }) =>
        row.getValue('fundAmount')
          ? `$${justFormatNumber(row.getValue('fundAmount'))}`
          : '--',
    },
    {
      accessorKey: 'reporterName',
      header: fm('contributors3'),
      cell: ({ row }) => {
        const { reporterName: address } = row?.original || {};
        return (
          <span className="font-mono">
            {/* 不暴露用户信息，后端会做脱敏省略 */}
            {address}
            {/* {address?.length <= 10
              ? address
              : maskAddress({
                  address,
                  before: 10,
                  after: 4,
                  mark: '...',
                })} */}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: fm('status1'),
      cell: ({ row }) => {
        const status = row.getValue('status');
        return (
          <span
            style={{
              backgroundColor: getReportStatusColor(status).bg,
              color: getReportStatusColor(status).text,
            }}
            className="px-2 py-1 rounded-md whitespace-nowrap"
          >
            {getReportStatusText(status) ? fm(getReportStatusText(status)) : ''}
          </span>
          // <StatusTag
          //   status={
          //     (row?.original?.status as unknown as SupportStatus) ||
          //     SupportStatus.FULL_PROVIDED
          //   }
          // />
        );
      },
    },
    {
      accessorKey: 'responseTime',
      header: fm('responseTime'),
      cell: ({ row }) => {
        const responseTime = row.original.entityResponseTime;
        const contactTime = row.original.contactEntityTime;

        if (responseTime) {
          return formatTimeDiff(responseTime - contactTime);
        }
        return formatTimeDiff(Date.now() - contactTime);
      },
    },
    {
      accessorKey: 'estBounty',
      header: fm('estBounty'),
      cell: ({ row }) =>
        row.getValue('estBounty')
          ? `$${justFormatNumber(row.getValue('estBounty'))}`
          : '--',
    },
    {
      accessorKey: 'action',
      header: fm('action'),
      cell: ({ row }) => (
        <button
          className="flex items-center gap-1 whitespace-nowrap"
          onClick={() => toDetail(row.original)}
        >
          {fm('viewDetail')}
          <span className="text-lg">›</span>
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="max-[768px]:hidden mt-4">
        <CommonLoading loading={loading}>
          <Table
            columns={columns}
            data={reportList}
            cellSpaceStyle="large"
            height="528px"
          />
        </CommonLoading>
      </div>
      <div className="min-[768px]:hidden">
        {loading && !reportList.length ? (
          // <div className="flex justify-center items-center h-96">
          //   <Spin theme="dark" />
          // </div>
          <CommonLoading loading={loading} />
        ) : (
          <div className="flex flex-col gap-2">
            {reportList.map((item, index) => (
              <div
                key={index}
                className="bg-bds-gray-bg-card rounded-lg p-3 flex flex-col gap-3"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {fm('contributors3')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        {/* 不暴露用户信息，后端会做脱敏省略 */}
                        {item.reporterName}
                        {/* {item.reporterName?.length <= 10
                          ? item.reporterName
                          : maskAddress({
                              address: item.reporterName,
                              before: 10,
                              after: 4,
                              mark: '...',
                            })} */}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 text-right">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {fm('receivedFunds')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        $
                        {item.fundAmount
                          ? justFormatNumber(item.fundAmount)
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-end justify-between">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-xs leading-[18px] text-bds-gray-t2">
                        {fm('time')}
                      </h3>
                      <p className="text-sm leading-[22px] font-semibold">
                        {dayjs(item.reportTime).format('YYYY-MM-DD HH:mm:ss')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 text-right">
                      <TextLink
                        target="_blank"
                        variant="secondary"
                        className="text-white"
                        opensWindow
                        closeIcon={true}
                        onClick={() => toDetail(item)}
                      >
                        {fm('viewDetail')} <ShareIcon className="w-4 h-4" />
                      </TextLink>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && !reportList.length && (
          <div className="min-h-[200px]">
            <Empty subTitle={fm('noRecords')}></Empty>
          </div>
        )}
      </div>
      {pageInfo?.totalNum > pageSize && (
        <Pagination
          current={page}
          total={pageInfo?.totalNum}
          pageSize={pageInfo?.pageSize}
          align="center"
          onChange={onChangePageNo}
          rootClassName="mt-4"
        />
      )}
      <DetailModal
        visible={visible}
        onClose={onClose}
        data={currentReport as unknown as Report}
        entity={currentReport.entityName}
      />
    </div>
  );
};
